// Shared save-timeout helper. Use around every isolated-client write on
// a busy page so a hung request fails with a meaningful error instead
// of an infinite spinner. Paired with useSaveDb (see useSaveDb.ts), the
// pair is the playbook "belt-and-braces" against auth-token-lock
// contention on tablets with many concurrent useQuery hooks.
//
// Why PromiseLike: supabase-js query builders are thenable but not
// strict Promises; accepting PromiseLike lets the caller pass the
// builder directly without an explicit await.

import { ValidationError } from '@/lib/errorReporter'
import { trackSlowSave, trackSaveTimeout, SLOW_SAVE_MS } from '@/lib/uiTelemetry'

// 30s comfortably covers a p99-healthy round-trip on the worst tablet
// observed. Anything past that is almost certainly stuck.
export const SAVE_TIMEOUT_MS = 30_000

// generate_payroll_period rebuilds every line for the period and measured 33.9 s
// for a 421-line month (Anchor, Oct 2026). It is the one call allowed to outwait
// the 30 s default. ⚠️ The SERVER still cancels it at the `authenticated` role's
// 30 s statement_timeout (mig 852); a function-level SET cannot extend a timer the
// caller's statement already started. A run that long needs the role limit raised
// by hand around it; this wait only stops the browser giving up first. The REST
// net in supabase.ts (LONG_RPC_TIMEOUT_MS) must carry the same bound, or it aborts
// the request at 30 s underneath this wait.
export const GENERATE_PAYROLL_TIMEOUT_MS = 180_000

// Dashboard "glance" widgets (Inventory Pulse, Packout trend): operators dwell
// ~6s here, so a 30s background-refresh hang is felt as a freeze and drives a
// full-page reload (UX-007). Fail to the inline "showing last known · Retry"
// affordance much sooner — the stale React Query data keeps showing meanwhile,
// and the 60s poll / realtime invalidation still self-heal. These reads are
// pure aggregates (get_inventory_pulse ≈ 200ms healthy), so 15s never clips a
// working call — it only shortens the stuck case.
export const DASHBOARD_WIDGET_TIMEOUT_MS = 15_000

// Reference/lookup READS (rosters, wage determinations, DOB checks): same
// hang risk, but they are not saves — logging them through trackSlowSave /
// trackSaveTimeout is what buried the save metrics under read noise
// (UX-035/036/038). A failed or timed-out read already reports itself as a
// query_error through the QueryCache listener in main.tsx, so withReadTimeout
// races the same timer WITHOUT emitting save telemetry. Reference reads are
// small lookups (healthy round-trip well under a second), so the short timer
// only shortens the stuck case.
export const REFERENCE_READ_TIMEOUT_MS = 15_000

// The tail of the sentence withTimeout throws. Exported so a caller can tell a
// STOPPED-WAITING failure apart from a refusal without re-typing the string:
// the two mean opposite things to an operator. A refusal is "nothing was
// written"; a timeout is "we stopped waiting and cannot tell" — and for a
// non-idempotent create (a goods receipt, a stock movement) that difference
// decides whether re-entering the capture duplicates it.
export const SAVE_TIMEOUT_SUFFIX = ' took too long — refresh the page and try again.'

/** True when `err` is the error withTimeout raises after its timer fires. */
export function isSaveTimeout(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  return msg.endsWith(SAVE_TIMEOUT_SUFFIX)
}

export function withTimeout<T>(
  p: PromiseLike<T>,
  label: string,
  timeoutMs: number = SAVE_TIMEOUT_MS,
  // Called once if (and only if) the timer fires first. Use it to ABORT the
  // underlying request — Promise.race never cancels the racing promise, so a
  // timed-out fetch otherwise keeps running and keeps hogging the connection
  // (the bug that made bounded-concurrency offline-prep pointless). Pass an
  // AbortController's `abort` here when the wrapped promise honours a signal.
  onTimeout?: () => void,
  trackAsSave = true,
): Promise<T> {
  // `settled` guards the timeout callback: Promise.race never cancels the
  // timer, so without it every successful save would log a phantom timeout
  // when the timer eventually fires. `timedOut` guards the mirror image: the
  // race never cancels the wrapped promise either, so a call that times out
  // and then resolves anyway would log BOTH save_timeout and slow_save for
  // the one incident (live: `billing status` slow_saves clustered at p50
  // 30,021ms — the just-past-the-timer resolutions).
  let settled = false
  let timedOut = false
  const t0 = performance.now()
  return Promise.race([
    Promise.resolve(p).then(v => {
      settled = true
      const elapsed = performance.now() - t0
      if (trackAsSave && !timedOut && elapsed > SLOW_SAVE_MS) trackSlowSave(label, elapsed)
      return v
    }, err => {
      settled = true
      throw err
    }),
    new Promise<T>((_, reject) => setTimeout(
      () => {
        if (!settled) {
          timedOut = true
          if (trackAsSave) trackSaveTimeout(label, timeoutMs)
          onTimeout?.()
        }
        reject(new ValidationError(`${label}${SAVE_TIMEOUT_SUFFIX}`))
      },
      timeoutMs,
    )),
  ])
}

export function withReadTimeout<T>(
  p: PromiseLike<T>,
  label: string,
  timeoutMs: number = REFERENCE_READ_TIMEOUT_MS,
  onTimeout?: () => void,
): Promise<T> {
  return withTimeout(p, label, timeoutMs, onTimeout, false)
}
